import React from "react";

import {
  BillingIcon,
  ProjectsIcon,
  SecurityIcon,
  SettingsIcon,
  TeamIcon,
  UserIcon
} from "@packages/ds";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import useUrls from "Hooks/useUrls";
import {
  selectFeatureBillingEnabled,
  selectFeatureUsageBasedBillingEnabled
} from "Reducers/featureFlags/featureFlags.selectors";
import { organizationByDescriptionIdSelector } from "Reducers/organization";
import { useIsOrganizationSecurityEnabled } from "src/organization/pages/settings/pages/security/hooks/useIsOrganizationSecurityEnabled";
import { useIsTeamsEnabled } from "src/organization/pages/settings/pages/teams/hooks/useIsTeamsEnabled";
import { useAppSelector } from "Store/hooks";

import { useCommandMenuContext } from "../hooks";

import { Highlighter } from "./Highlighter";
import { Item } from "./Item";

export function OrganizationActions({
  organizationId
}: {
  organizationId: string;
}) {
  const navigate = useNavigate();
  const intl = useIntl();
  const billingEnabled = useAppSelector(selectFeatureBillingEnabled);
  const [isSecurityEnabled] = useIsOrganizationSecurityEnabled(organizationId);
  const organizationTeamsStatus = useIsTeamsEnabled();
  const isUsageBasedBillingEnabled = useAppSelector(
    selectFeatureUsageBasedBillingEnabled
  );
  const selectedOrganization = useAppSelector(state =>
    organizationByDescriptionIdSelector(state, {
      organizationDescriptionId: organizationId
    })
  );
  const hasMemberPermission = !!selectedOrganization?.hasLink("members");

  const [
    organizationSecurityUrl,
    organizationUsersUrl,
    organizationTeamsUrl,
    organizationSettingsUrl,
    organizationBillingUrl
  ] = useUrls(
    [
      "organization.settings.security",
      "organization.settings.users",
      "organization.settings.teams",
      "organization.settings",
      "organization.billing"
    ],
    { organizationId: organizationId ?? "" }
  );
  const { inputValue } = useCommandMenuContext();

  const shouldShowBillingItem =
    billingEnabled &&
    (selectedOrganization?.hasLink("orders") ||
      selectedOrganization?.hasLink("estimate-subscription"));

  return (
    <>
      <Item
        value={intl.formatMessage({ id: "projects" })}
        onSelect={() => navigate(`/${encodeURI(organizationId)}`)}
      >
        <ProjectsIcon />
        <Highlighter
          searchText={inputValue}
          textToHighlight={intl.formatMessage({ id: "projects" })}
        />
      </Item>
      <Item
        value={intl.formatMessage({ id: "settings" })}
        onSelect={() => navigate(organizationSettingsUrl)}
      >
        <SettingsIcon color="var(--mode-vector-neutral-default)" />
        <Highlighter
          searchText={inputValue}
          textToHighlight={intl.formatMessage({ id: "settings" })}
        />
      </Item>
      {shouldShowBillingItem && (
        <Item
          value={intl.formatMessage({ id: "menu.billing" })}
          onSelect={() => navigate(organizationBillingUrl)}
        >
          <BillingIcon />
          <Highlighter
            searchText={inputValue}
            textToHighlight={intl.formatMessage({ id: "menu.billing" })}
          />
        </Item>
      )}
      {(isSecurityEnabled || isUsageBasedBillingEnabled) && (
        <Item
          value={intl.formatMessage({ id: "organizations.security.title" })}
          onSelect={() => navigate(organizationSecurityUrl)}
        >
          <SecurityIcon />
          <Highlighter
            searchText={inputValue}
            textToHighlight={intl.formatMessage({
              id: "organizations.security.title"
            })}
          />
        </Item>
      )}
      {["enabled", "disabled-promote-links"].includes(
        organizationTeamsStatus
      ) &&
        hasMemberPermission && (
          <Item
            value={intl.formatMessage({ id: "menu.teams" })}
            onSelect={() => navigate(organizationTeamsUrl)}
          >
            <TeamIcon />
            <Highlighter
              searchText={inputValue}
              textToHighlight={intl.formatMessage({
                id: "menu.teams"
              })}
            />
          </Item>
        )}
      {!!selectedOrganization?.hasLink("members") && (
        <Item
          onSelect={() => navigate(organizationUsersUrl)}
          value={intl.formatMessage({ id: "menu.users" })}
        >
          <UserIcon color="var(--mode-vector-neutral-default)" />
          <Highlighter
            searchText={inputValue}
            textToHighlight={intl.formatMessage({
              id: "menu.users"
            })}
          />
        </Item>
      )}
    </>
  );
}
